import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, inject, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { HonorificTitle } from '../../../models/honorificTitle';
import { Nationality } from '../../../models/nationality';
import { Profession } from '../../../models/profession';
import { IndustryExpertise } from '../../../models/industryExpertise';
import { Qualification } from '../../../models/qualification';
import { FinancialExpertise } from '../../../models/financialExpertise';
import { PoliticalPosition } from '../../../models/politicalPosition';
import { EducationField } from '../../../models/educationField';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { WorkExperience } from '../../../models/workExperience';
import { TrainingAndEducation } from '../../../models/trainingAndEducation';
import { WorkExperienceSelectionDialogComponent } from '../work-experience-selection-dialog/work-experience-selection-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TrainingEducationSelectionDialogComponent } from '../training-education-selection-dialog/training-education-selection-dialog.component';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { DirectorTrainingOrEducationType } from '../../../models/enums/directorTrainingOrEducationType.enum';
import { RouterLink } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { InfoDialogService } from '../../../services/info-dialog.service';
import { DirectorPositionsResponseDto, DirectorPositionDto } from '../../../models/api/directorPositionsDto';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

export interface PositionsByYear {
  year: number;
  positions: DirectorPositionDto[];
}

export interface PositionsByCompany {
  companyId: string;
  companyName: string;
  positions: DirectorPositionDto[];
}

export const DD_MM_YYYY_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override parse(value: any): Date | null {
    if (!value) return null;

    if (typeof value === 'string') {
      // Handle DD/MM/YYYY format
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          return new Date(year, month, day);
        }
      }
    }

    return super.parse(value);
  }

  override format(date: Date, displayFormat: Object): string {
    if (!date) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }
}

@Component({
  selector: 'app-director-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    RouterLink,
    GenericListComponent,
    TextNormalizeDirective
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMAT }
  ],
  templateUrl: './director-form.component.html',
  styleUrls: ['./director-form.component.scss']
})
export class DirectorFormComponent implements OnInit, OnDestroy {
  @ViewChild('workExperienceList') workExperienceList?: GenericListComponent<WorkExperience>;
  @ViewChild('trainingAndEducationList') trainingAndEducationList?: GenericListComponent<TrainingAndEducation>;
  @Input() form!: FormGroup;
  @Input() submitButtonText = 'Submit';
  @Input() isEditMode = false;
  @Input() hideActions = false;
  @Input() openLinksInNewTab = false;
  @Input() set positions(value: DirectorPositionsResponseDto | null) {
    this._positions = value;
    this.boardPositionsByYear = this.groupByYear(value?.boardPositions || []);
    this.executivePositionsByYear = this.groupByYear(value?.executivePositions || []);
    this.boardPositionsByCompany = this.groupByCompany(value?.boardPositions || []);
    this.executivePositionsByCompany = this.groupByCompany(value?.executivePositions || []);
  }
  @Output() submit = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  selectedTabIndex = 0;
  _positions: DirectorPositionsResponseDto | null = null;
  positionsViewMode: 'year' | 'company' = 'year';
  boardPositionsByYear: PositionsByYear[] = [];
  executivePositionsByYear: PositionsByYear[] = [];
  boardPositionsByCompany: PositionsByCompany[] = [];
  executivePositionsByCompany: PositionsByCompany[] = [];

  boardDisplayedColumns: string[] = [
    'companyName', 'classification', 'occupationName', 'tenure',
    'meetingAttendancePercentage', 'forVotesPct', 'againstVotesPct',
    'auditCommitteeRole', 'remunerationCommitteeRole', 'nominationCommitteeRole',
    'cgCommitteeRole', 'shares'
  ];

  boardByCompanyDisplayedColumns: string[] = [
    'year', 'classification', 'occupationName', 'tenure',
    'meetingAttendancePercentage', 'forVotesPct', 'againstVotesPct',
    'auditCommitteeRole', 'remunerationCommitteeRole', 'nominationCommitteeRole',
    'cgCommitteeRole', 'shares'
  ];

  executiveDisplayedColumns: string[] = [
    'companyName', 'occupationName', 'tenure', 'shares'
  ];

  executiveByCompanyDisplayedColumns: string[] = [
    'year', 'occupationName', 'tenure', 'shares'
  ];

  // Flags for tracking update sources to prevent circular updates
  private isUpdatingAge = false;
  private isUpdatingYearOfBirth = false;

  // Current year for age calculations
  private currentYear = new Date().getFullYear();

  private lookupService = inject(LookupDataService);

  titleAutoFilled = false;

  genderOptions = [
    { value: 0, label: 'Unknown' },
    { value: 1, label: 'Male' },
    { value: 2, label: 'Female' }
  ];

  honorificTitles: HonorificTitle[] = [];
  nationalities: Nationality[] = [];
  professions: Profession[] = [];
  industryExpertises: IndustryExpertise[] = [];
  qualifications: Qualification[] = [];
  financialExpertises: FinancialExpertise[] = [];
  politicalPositions: PoliticalPosition[] = [];
  educationFields: EducationField[] = [];
  selectedPhotoName: string | null = null;
  selectedPhotoUrl: string | null = null;
  existingPhotoUrl: string | null = null;
  photoWasRemoved: boolean = false;

  trainingAndEducationColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'type', displayName: 'Type', dataType: 'enum', enumType: DirectorTrainingOrEducationType },
    { propertyName: 'educationField', displayName: 'Education Field', dataType: 'object' },
    { propertyName: 'university', displayName: 'University', dataType: 'object' }
  ];

  trainingAndEducations: TrainingAndEducation[] = [];

  workExperienceColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'company', displayName: 'Company', dataType: 'object' },
    {
      propertyName: 'companyType',
      displayName: 'Source',
      dataType: 'badge',
      badgeMap: {
        workExperienceCompany: { label: 'Work Experience', cssClass: 'type-director' },
        company: { label: 'Companies List', cssClass: 'type-company' },
        opbCompany: { label: 'OPB Companies', cssClass: 'type-opb' }
      }
    },
    { propertyName: 'position', displayName: 'Position', dataType: 'object' },
    { propertyName: 'startDateDisplay', displayName: 'Start Date', dataType: 'string' },
    { propertyName: 'endDateDisplay', displayName: 'End Date', dataType: 'string' }
  ];

  private _workExperiences: WorkExperience[] = [];
  get workExperiences(): WorkExperience[] {
    return this._workExperiences;
  }
  set workExperiences(value: WorkExperience[]) {
    this._workExperiences = this.sortWorkExperiences(value || []).map(we => ({
      ...we,
      startDateDisplay: this.formatDateForList(we.startDate),
      endDateDisplay: this.formatEndDateForList(we)
    }));
  }

  private formatDateForList(value: Date | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
  }

  private formatEndDateForList(we: WorkExperience): string {
    if (we.isPresent) return 'Present';
    return this.formatDateForList(we.endDate);
  }

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private dialog: MatDialog,
    private infoDialogService: InfoDialogService
  ) {
    // Load all lookup data from lookup service
    this.lookupService.getTableStream<Nationality>('nationalities').subscribe(data => {
      if (data) this.nationalities = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lookupService.getTableStream<HonorificTitle>('honorificTitles').subscribe(data => {
      if (data) this.honorificTitles = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lookupService.getTableStream<Profession>('professions').subscribe(data => {
      if (data) this.professions = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lookupService.getTableStream<IndustryExpertise>('industryExpertises').subscribe(data => {
      if (data) this.industryExpertises = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lookupService.getTableStream<Qualification>('qualifications').subscribe(data => {
      if (data) this.qualifications = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lookupService.getTableStream<FinancialExpertise>('financialExpertises').subscribe(data => {
      if (data) this.financialExpertises = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lookupService.getTableStream<PoliticalPosition>('politicalPositions').subscribe(data => {
      if (data) this.politicalPositions = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lookupService.getTableStream<EducationField>('educationFields').subscribe(data => {
      if (data) this.educationFields = data.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  ngOnInit() {
    this.setupAgeYearOfBirthSync();
    this.setupExistingPhoto();

    // Initialize arrays
    this.workExperiences = this.form.get('workExperiences')?.value || [];
    this.trainingAndEducations = this.form.get('trainingAndEducations')?.value || [];

    // Subscribe to form value changes to update the arrays when data is loaded
    this.form.get('workExperiences')?.valueChanges.subscribe(value => {
      this.workExperiences = value || [];
    });

    this.form.get('trainingAndEducations')?.valueChanges.subscribe(value => {
      this.trainingAndEducations = value || [];
    });

    // Auto-select title when gender changes: set if empty, or swap if it's the opposite-gender title.
    // Gender-neutral titles (e.g. Dr.) are left untouched.
    this.form.get('gender')?.valueChanges.subscribe(gender => {
      const currentTitle = this.form.get('honorificTitle')?.value;
      const currentTitleName = currentTitle?.name;

      const setTitleWithFlash = (newTitle: any) => {
        this.form.get('honorificTitle')?.setValue(newTitle);
        this.titleAutoFilled = true;
        setTimeout(() => this.titleAutoFilled = false, 2000);
      };

      if (gender === 1) { // Male
        const isFemaleTitle = currentTitleName === 'Ms.' || currentTitleName === 'Mrs.';
        if (!currentTitle || isFemaleTitle) {
          const mrTitle = this.honorificTitles.find(title => title.name === 'Mr.');
          if (mrTitle) {
            setTitleWithFlash(mrTitle);
          }
        }
      } else if (gender === 2) { // Female
        const isMaleTitle = currentTitleName === 'Mr.';
        if (!currentTitle || isMaleTitle) {
          const msTitle = this.honorificTitles.find(title => title.name === 'Ms.');
          if (msTitle) {
            setTitleWithFlash(msTitle);
          } else {
            const mrsTitle = this.honorificTitles.find(title => title.name === 'Mrs.');
            if (mrsTitle) {
              setTitleWithFlash(mrsTitle);
            }
          }
        }
      }
    });
  }

  ngOnDestroy() {
    // Clean up object URL to prevent memory leaks
    if (this.selectedPhotoUrl) {
      URL.revokeObjectURL(this.selectedPhotoUrl);
    }
  }

  private setupAgeYearOfBirthSync() {
    // Year of birth changes -> update age
    this.form.get('yearOfBirth')?.valueChanges.subscribe(yearOfBirth => {
      if (this.isUpdatingYearOfBirth) return;

      if (yearOfBirth && yearOfBirth > 0) {
        const refYear = this.getReferenceYear();
        const calculatedAge = refYear - yearOfBirth;
        if (calculatedAge >= 18 && calculatedAge <= 110) {
          this.isUpdatingAge = true;
          this.form.get('age')?.setValue(calculatedAge, { emitEvent: false });
          this.isUpdatingAge = false;
        }
      }
    });

    // Age changes -> update year of birth
    this.form.get('age')?.valueChanges.subscribe(age => {
      if (this.isUpdatingAge) return;

      if (age && age >= 18 && age <= 110) {
        const refYear = this.getReferenceYear();
        const calculatedYearOfBirth = refYear - age;
        if (calculatedYearOfBirth > 1800 && calculatedYearOfBirth <= this.currentYear) {
          this.isUpdatingYearOfBirth = true;
          this.form.get('yearOfBirth')?.setValue(calculatedYearOfBirth, { emitEvent: false });
          this.isUpdatingYearOfBirth = false;
        }
      }
    });

    // Deceased status or death date changes -> recalculate age (keep year of birth)
    this.form.get('isDeceased')?.valueChanges.subscribe(() => {
      this.recalculateAgeFromYearOfBirth();
      this.updateAgeFieldState();
    });

    this.form.get('dateOfDeath')?.valueChanges.subscribe(() => {
      this.recalculateAgeFromYearOfBirth();
      this.updateAgeFieldState();
    });

    // Year of birth confirmed changes -> update age field state
    this.form.get('yearOfBirthConfirmed')?.valueChanges.subscribe(() => {
      this.updateAgeFieldState();
    });

    // Initialize age field state
    this.updateAgeFieldState();
  }

  updateAgeFieldState() {
    const isDeceased = this.form.get('isDeceased')?.value;
    const dateOfDeath = this.form.get('dateOfDeath')?.value;
    const yearOfBirthConfirmed = this.form.get('yearOfBirthConfirmed')?.value;
    const ageControl = this.form.get('age');

    if ((isDeceased && dateOfDeath) || yearOfBirthConfirmed) {
      ageControl?.disable({ emitEvent: false });
    } else {
      ageControl?.enable({ emitEvent: false });
    }
  }

  private getReferenceYear(): number {
    const isDeceased = this.form.get('isDeceased')?.value;
    const dateOfDeath = this.form.get('dateOfDeath')?.value;

    if (isDeceased && dateOfDeath) {
      // Handle both Date objects and date strings (e.g., "2016-11-27")
      const deathDate = dateOfDeath instanceof Date ? dateOfDeath : new Date(dateOfDeath);
      if (!isNaN(deathDate.getTime())) {
        return deathDate.getFullYear();
      }
    }
    return this.currentYear;
  }

  private recalculateAgeFromYearOfBirth() {
    const yearOfBirth = this.form.get('yearOfBirth')?.value;
    if (yearOfBirth && yearOfBirth > 0) {
      const refYear = this.getReferenceYear();
      const calculatedAge = refYear - yearOfBirth;
      if (calculatedAge >= 18 && calculatedAge <= 110) {
        this.isUpdatingAge = true;
        this.form.get('age')?.setValue(calculatedAge, { emitEvent: false });
        this.isUpdatingAge = false;
      }
    }
  }

  private setupExistingPhoto() {
    // Check if there's an existing photo filename in the form
    const existingPhotoFileName = this.form.get('photoFileName')?.value;
    if (existingPhotoFileName && typeof existingPhotoFileName === 'string') {
      this.existingPhotoUrl = `assets/images/${existingPhotoFileName}`;
      this.selectedPhotoName = existingPhotoFileName;
    }
  }

  // Angular leaves a control pristine when it is written programmatically, so every
  // edit made through a dialog or the photo picker has to flag itself. Route those
  // writes through here and `form.dirty` stays a trustworthy answer for the
  // unsaved-changes guard.
  private setValueAsDirty(controlName: string, value: unknown): void {
    const control = this.form.get(controlName);
    control?.setValue(value);
    control?.markAsDirty();
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  onSubmit() {
    if (this.form.valid) {
      const confirmationDialog = this.isEditMode
        ? this.confirmationDialogService.openUpdateConfirmationDialog()
        : this.confirmationDialogService.openSaveConfirmationDialog();

      confirmationDialog.subscribe(result => {
        if (result) {
          this.submit.emit();
        }
      });
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Get configuration from environment
      const { maxFileSizeBytes, allowedFileTypes, maxImageWidth, maxImageHeight } = environment.photoUpload;

      // Validate file type
      if (!allowedFileTypes.includes(file.type)) {
        this.infoDialogService.openErrorDialog(
          `Invalid file type. Please select one of the following: ${allowedFileTypes.join(', ')}`,
          'Invalid File Type'
        );
        input.value = ''; // Clear the input
        return;
      }

      // Validate file size
      if (file.size > maxFileSizeBytes) {
        const maxSizeMB = (maxFileSizeBytes / (1024 * 1024)).toFixed(2);
        this.infoDialogService.openErrorDialog(
          `File size exceeds the maximum allowed size of ${maxSizeMB}MB. Please select a smaller file.`,
          'File Too Large'
        );
        input.value = ''; // Clear the input
        return;
      }

      // Validate image dimensions
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        // Clean up the temporary URL
        URL.revokeObjectURL(objectUrl);

        if (img.width > maxImageWidth || img.height > maxImageHeight) {
          this.infoDialogService.openErrorDialog(
            `Image dimensions exceed the maximum allowed size of ${maxImageWidth}x${maxImageHeight}px. Please select a smaller image.`,
            'Image Too Large'
          );
          input.value = ''; // Clear the input
          return;
        }

        // All validations passed, proceed with the file
        // Clean up previous URL to prevent memory leaks
        if (this.selectedPhotoUrl) {
          URL.revokeObjectURL(this.selectedPhotoUrl);
        }

        this.selectedPhotoName = file.name;

        // Create object URL for preview
        this.selectedPhotoUrl = URL.createObjectURL(file);

        // Clear existing photo URL since we have a new photo
        this.existingPhotoUrl = null;

        // Add the file to the form control
        this.setValueAsDirty('photoFileName', file);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        this.infoDialogService.openErrorDialog(
          'Failed to load the image. Please select a valid image file.',
          'Invalid Image'
        );
        input.value = ''; // Clear the input
      };

      img.src = objectUrl;
    }
  }

  onRemovePhoto() {
    // Clean up object URL to prevent memory leaks
    if (this.selectedPhotoUrl) {
      URL.revokeObjectURL(this.selectedPhotoUrl);
    }

    // Track that a photo was removed if there was an existing one
    if (this.existingPhotoUrl) {
      this.photoWasRemoved = true;
    }

    this.selectedPhotoName = null;
    this.selectedPhotoUrl = null;
    this.existingPhotoUrl = null;

    // Clear the form control
    this.setValueAsDirty('photoFileName', null);
  }

  onPhotoClick() {
    const photoUrl = this.getCurrentPhotoUrl();
    if (photoUrl) {
      window.open(photoUrl, '_blank');
    }
  }

  onChoosePhotoClick(event: Event, fileInput: HTMLInputElement) {
    event.preventDefault();
    event.stopPropagation();
    // Reset the value so the same file can be selected again
    fileInput.value = '';
    fileInput.click();
  }

  onFileInputCancel(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Method to get the photo from the form
  getPhotoFromForm(): File | null {
    const value = this.form.get('photoFileName')?.value;
    return (value instanceof File) ? value : null;
  }

  // Method to get the existing photo filename
  getExistingPhotoFileName(): string | null {
    const value = this.form.get('photoFileName')?.value;
    return (typeof value === 'string') ? value : null;
  }

  // Method to check if a photo exists in the form (either new file or existing filename)
  hasPhoto(): boolean {
    return !!this.form.get('photoFileName')?.value || !!this.existingPhotoUrl;
  }

  // Method to get the current photo URL for display (either existing or newly selected)
  getCurrentPhotoUrl(): string | null {
    return this.selectedPhotoUrl || this.existingPhotoUrl;
  }

  // Method to check if there's a newly selected photo
  hasNewPhoto(): boolean {
    return !!this.selectedPhotoUrl;
  }

  // Method to check if there's an existing photo
  hasExistingPhoto(): boolean {
    return !!this.existingPhotoUrl;
  }

  // Method to check if a photo was removed
  wasPhotoRemoved(): boolean {
    return this.photoWasRemoved;
  }

  // Method to reset the photo removed flag
  resetPhotoRemovedFlag(): void {
    this.photoWasRemoved = false;
  }

  onItemSelected(item: EducationField) {
  }

  onAddWorkExperience() {
    const dialogRef = this.dialog.open(WorkExperienceSelectionDialogComponent, {
      width: '720px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.workExperiences = [...this.workExperiences, result];
        this.setValueAsDirty('workExperiences', this.workExperiences);
      }
    });
  }

  onEditWorkExperience(workExperience: WorkExperience) {
    const dialogRef = this.dialog.open(WorkExperienceSelectionDialogComponent, {
      width: '720px',
      data: workExperience,
      autoFocus: 'dialog'
    });

    // generic-list flips the row into inline-edit mode right after emitting editClick.
    // Defer to a microtask so we run after that state is set, then clear it.
    queueMicrotask(() => this.workExperienceList?.cancelEdit());

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.workExperiences = this.workExperiences.map(w => w.id === result.id ? result : w);
        this.setValueAsDirty('workExperiences', this.workExperiences);
      }
    });
  }

  onDeleteWorkExperience(workExperience: WorkExperience) {
    this.confirmationDialogService.openDeleteConfirmationDialog('Work Experience Company: ' + workExperience.company.name + ' - Position: ' + workExperience.position.name)
    .subscribe(result => {
      if (result) {
        this.workExperiences = this.workExperiences.filter(w => w.id !== workExperience.id);
        this.setValueAsDirty('workExperiences', this.workExperiences);
      }
    });
  }

  private sortWorkExperiences(items: WorkExperience[]): WorkExperience[] {
    return [...items].sort((a, b) => {
      if (!!a.isPresent !== !!b.isPresent) return a.isPresent ? -1 : 1;
      const aTime = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bTime = b.startDate ? new Date(b.startDate).getTime() : 0;
      return bTime - aTime;
    });
  }

  onAddTrainingAndEducation() {
    const dialogRef = this.dialog.open(TrainingEducationSelectionDialogComponent, {
      width: '720px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.trainingAndEducations = [...this.trainingAndEducations, result];
        this.setValueAsDirty('trainingAndEducations', this.trainingAndEducations);
      }
    });
  }

  onEditTrainingAndEducation(trainingAndEducation: TrainingAndEducation) {
    const dialogRef = this.dialog.open(TrainingEducationSelectionDialogComponent, {
      width: '720px',
      data: trainingAndEducation,
      autoFocus: 'dialog'
    });

    queueMicrotask(() => this.trainingAndEducationList?.cancelEdit());

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.trainingAndEducations = this.trainingAndEducations.map(t => t.id === result.id ? result : t);
        this.setValueAsDirty('trainingAndEducations', this.trainingAndEducations);
      }
    });
  }

  onDeleteTrainingAndEducation(trainingAndEducation: TrainingAndEducation) {
    this.confirmationDialogService.openDeleteConfirmationDialog('Training and Education Field: ' + trainingAndEducation.educationField?.name + ' - University: ' + trainingAndEducation.university?.name)
    .subscribe(result => {
      if (result) {
        this.trainingAndEducations = this.trainingAndEducations.filter(t => t.id !== trainingAndEducation.id);
        this.setValueAsDirty('trainingAndEducations', this.trainingAndEducations);
      }
    });
  }

  // Compare function for mat-select to compare objects by ID
  compareById(option1: any, option2: any): boolean {
    return option1 && option2 ? option1.id === option2.id : option1 === option2;
  }

  // Clear an optional select field via the suffix "X" button
  clearField(controlName: string): void {
    const control = this.form.get(controlName);
    control?.setValue(null);
    control?.markAsDirty();
  }

  private groupByYear(positions: DirectorPositionDto[]): PositionsByYear[] {
    const map = new Map<number, DirectorPositionDto[]>();
    for (const pos of positions) {
      if (!map.has(pos.year)) {
        map.set(pos.year, []);
      }
      map.get(pos.year)!.push(pos);
    }
    // Sort years descending (most recent first), positions within each year by company name
    return Array.from(map.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, items]) => ({
        year,
        positions: items.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''))
      }));
  }

  private groupByCompany(positions: DirectorPositionDto[]): PositionsByCompany[] {
    const map = new Map<string, { companyName: string; positions: DirectorPositionDto[] }>();
    for (const pos of positions) {
      if (!map.has(pos.companyId)) {
        map.set(pos.companyId, { companyName: pos.companyName, positions: [] });
      }
      map.get(pos.companyId)!.positions.push(pos);
    }
    return Array.from(map.entries())
      .sort(([, a], [, b]) => a.companyName.localeCompare(b.companyName))
      .map(([companyId, data]) => ({
        companyId,
        companyName: data.companyName,
        positions: data.positions.sort((a, b) => b.year - a.year)
      }));
  }

  getClassificationLabel(value?: number): string {
    if (value == null) return '';
    const labels: Record<number, string> = { 0: 'Unknown', 1: 'Executive', 2: 'Non-Executive', 3: 'Independent' };
    return labels[value] ?? '';
  }

  getAuditRoleLabel(value?: number): string {
    if (value == null) return '';
    const labels: Record<number, string> = { 1: 'OK', 2: 'CH', 3: 'FE', 4: 'CH-FE' };
    return labels[value] ?? '';
  }

  getCommitteeRoleLabel(value?: number): string {
    if (value == null) return '';
    const labels: Record<number, string> = { 1: 'OK', 2: 'CH' };
    return labels[value] ?? '';
  }

  getCompanyUrl(companyId: string, queryParams?: Record<string, string | number>): string {
    let url = `/companies/edit/${companyId}`;
    if (queryParams) {
      const stringParams: Record<string, string> = {};
      for (const [key, value] of Object.entries(queryParams)) {
        stringParams[key] = String(value);
      }
      const params = new URLSearchParams(stringParams).toString();
      if (params) url += `?${params}`;
    }
    return url;
  }

  formatPercentage(value?: number): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }

  formatNumber(value?: number): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
}
